<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { insertIntoTextArea1 } from "@common/Common";
    import { ScoringAlternativeReadModel, ScoringAttributeReadModel, ScoringStatusReadModel } from "@Evaluation/AlternativesSurveys/Domain/readmodels";
    import { SurveyAlternativesManagerDialogSaveEventPayload, SurveyAlternativesManagerDialogViewModel } from "@Evaluation/AlternativesSurveys/Components/SurveyAlternativesManagerDialog";

    @Component({
        props: {
            viewModel: { type: Object, required: true }
        }
    })
    export default class SurveyAlternativesManagerDialog extends Vue
    {
        get vm(): SurveyAlternativesManagerDialogViewModel { return this.$props.viewModel as SurveyAlternativesManagerDialogViewModel; }
        get opened(): boolean { return this.vm?.opened ?? false; }

        get initialized(): boolean { return this.vm.initialized; }
        get filtering(): boolean { return this.vm.filtering; }
        
        get alternativesLabel(): string { return this.vm.alternativesLabel; }
        get attributes(): ScoringAttributeReadModel[] { return this.vm.attributes; }
        get statuses(): ScoringStatusReadModel[] { return this.vm.statuses; }
        get lists(): ScoringListReadModel[] { return this.vm.lists; }
        get filterLabel(): string { return this.vm.filterLabel; }

        get surveyId(): number { return this.vm.surveyId; }
        get surveyName(): string { return this.vm.surveyName; }
        get includedAlternatives(): ScoringAlternativeReadModel[] { return this.vm.includedAlternatives; }
        get alternativesView(): ScoringAlternativeReadModel[] { return this.vm.alternativesView; }
        get alternativeDescriptionPreview(): string | null { return this.vm.alternativeDescriptionPreview; }

        get searchPhrase(): string | null { return this.vm.searchPhrase; }
        set searchPhrase(val: string | null) { this.vm.searchPhrase = val; }

        get activeTab(): string { return this.vm.activeTab; }
        set activeTab(val: string) { this.vm.activeTab = val; }

        get previewMode(): boolean { return this.vm.previewMode; }
        set previewMode(val: boolean) { this.vm.previewMode = val; }

        get descriptionTemplate(): string | null { return this.vm.descriptionTemplate; }
        set descriptionTemplate(val: string | null) { this.vm.descriptionTemplate = val; }

        insertIntoDescriptionTemplate(txt: string)
        {
            insertIntoTextArea1(this.$refs.descriptionTemplateTextArea as HTMLTextAreaElement, `{${txt}}`);
            (this.$refs.descriptionTemplateTextArea as any).dispatchEvent(new Event('input'));
        }

        includeAll() { this.alternativesView.forEach(a => a.IsIncluded = true); }
        excludeAll() { this.alternativesView.forEach(a => a.IsIncluded = false); }

        clearFilter() { this.vm.clearFilter(); }        
        filterIncluded() { this.vm.filterIncluded(); }
        filterExcluded() { this.vm.filterExcluded(false); }
        filterByStatus(statusId: number, statusName: string) { this.vm.filterByStatus(statusId, statusName); }
        filterByList(listId: string, listName: string)
        {
            this.vm.filterByList(listId, listName);
            this.$emit('load-alternatives', listId);
        }


        showDescriptionPreview(alternativeId: string)
        {
            this.previewMode = true;
            this.vm.alternativeDescriptionPreview = null;
            this.$emit('preview-description', alternativeId, this.descriptionTemplate);
        }

        handleConfirm()
        {
            const payload = new SurveyAlternativesManagerDialogSaveEventPayload(this.surveyId, this.descriptionTemplate, this.includedAlternatives.map(a => a.AlternativeId));
            this.$emit('save-survey', payload);
        }

        closeDialog()
        {
            this.vm.close();
        }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content" style="height: calc(100vh - 50px);">
                    <div class="modal-header">
                        <h5 class="modal-title">Manage <span class="text-lowercase">{{alternativesLabel}}</span></h5>
                        <button @click="closeDialog" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="initialized === false"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>
                        <div v-if="initialized">
                            <ul class="nav nav-tabs" role="tablist">
                                <li class="nav-item">
                                    <a class="nav-link" @click="activeTab = 'Alternatives'" :class="{'active': activeTab === 'Alternatives'}">Select</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" @click="activeTab = 'Template'" :class="{'active': activeTab === 'Template'}">Define description</a>
                                </li>
                            </ul>

                            <div class="tab-content">
                                <div :class="{'active': activeTab === 'Alternatives'}" class="tab-pane pt-3">

                                    <div class="alert alert-info alert-dismissible" role="alert">
                                        <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                        Select which <span class="text-lowercase italic">{{alternativesLabel}}</span> are included in your panel.
                                        You can use the filter or search to find the <span class="text-lowercase italic">{{alternativesLabel}}</span> you want to include,
                                        then check the <span class="text-lowercase italic">{{alternativesLabel}}</span>
                                        you want (one-by-one or in bulk using the gear-wheel).
                                        <strong>WARNING: New <span class="text-lowercase italic">{{alternativesLabel}}</span> won't be included in panels automatically.</strong>
                                    </div>

                                    <div class="row">
                                        <div class="col-md-6">
                                            <div class="dropdown">
                                                <a class="btn btn-outline-secondary btn-lg btn-block" data-toggle="dropdown">
                                                    Filter by: {{filterLabel ?? 'None'}}
                                                    <span class="caret"></span>
                                                </a>

                                                <div class="dropdown-menu" style="width: 100%; max-height: 250px; overflow-y: auto;">
                                                    <a class="dropdown-item" @click="clearFilter">No filter</a>
                                                    <div class="dropdown-divider"></div>

                                                    <a class="dropdown-item" @click="filterIncluded">Included in the survey</a>
                                                    <a class="dropdown-item" @click="filterExcluded">Not included in the survey</a>
                                                    <div class="dropdown-divider"></div>

                                                    <div class="dropdown-header">Filter by status:</div>
                                                    <a class="dropdown-item" v-for="s in statuses" :key="s.Id" @click="filterByStatus(s.Id, s.Name)">
                                                        {{s.Name}}
                                                    </a>
                                                    <div class="dropdown-divider"></div>

                                                    <div class="dropdown-header">Filter by list:</div>
                                                    <a class="dropdown-item" v-for="l in lists" :key="l.Id" @click="filterByList(l.Id, l.Name)">
                                                        {{l.Name}}
                                                    </a>

                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <div class="input-group input-group-lg">
                                                <input type="search" placeholder="Search..." class="form-control" v-model="searchPhrase">
                                                <span class="input-group-append">
                                                    <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <br />
                                    <h3 v-if="filtering === true">Loading...</h3>
                                    <table class="table table-bordered" v-if="filtering === false">
                                        <thead>
                                            <tr>
                                                <th style="width: 45px;" class="text-center">
                                                    <div class="dropdown">
                                                        <a class="text-primary" data-toggle="dropdown">
                                                            <i class="fa fa-cog fa-lg"></i>
                                                        </a>
                                                        <div class="dropdown-menu">
                                                            <a class="dropdown-item" @click="includeAll">Include all listed below</a>
                                                            <a class="dropdown-item" @click="excludeAll">Exclude all listed below</a>
                                                        </div>
                                                    </div>
                                                </th>
                                                <th>Name</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="a in alternativesView" :key="a.Id">
                                                <td class="text-center">
                                                    <a class="text-primary" @click="a.IsIncluded = !a.IsIncluded">
                                                        <i v-if="a.IsIncluded === true" class="fa-solid fa-square-check"></i>
                                                        <i v-if="a.IsIncluded === false" class="fa-regular fa-square"></i>
                                                    </a>
                                                </td>
                                                <td>
                                                    {{a.Name}}
                                                </td>
                                                <td>
                                                    {{a.StatusName}}
                                                </td>

                                            </tr>

                                        </tbody>
                                    </table>
                                </div>
                                <div :class="{'active': activeTab === 'Template' }" class="tab-pane pt-3">

                                    <div v-if="previewMode === true">

                                        <div class="d-flex justify-content-end">
                                            <button class="btn btn-sm btn-secondary" @click="previewMode = false">Edit template</button>

                                            <div class="dropdown ml-1" v-if="includedAlternatives?.length">
                                                <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Preview</button>
                                                <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                                    <a class="dropdown-item" v-for="a in includedAlternatives" :key="a.Id" @click="showDescriptionPreview(a.Id)">
                                                        {{a.Name}}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-html="alternativeDescriptionPreview"></div>
                                    </div>

                                    <div v-if="previewMode === false">
                                        <div class="d-flex justify-content-end">
                                            <div class="dropdown">
                                                <button class="btn btn-sm btn-secondary dropdown-toggle" data-toggle="dropdown">Insert</button>
                                                <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                                    <a class="dropdown-item" @click="insertIntoDescriptionTemplate('Description')">Description</a>
                                                    <a class="dropdown-item" @click="insertIntoDescriptionTemplate('Status')">Status</a>
                                                    <a class="dropdown-item" v-for="attr in attributes" :key="attr.Id" @click="insertIntoDescriptionTemplate(attr.Name)">
                                                        {{attr.Name}}
                                                    </a>
                                                </div>
                                            </div>

                                            <div class="dropdown ml-1" v-if="includedAlternatives?.length">
                                                <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Preview</button>
                                                <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                                    <a class="dropdown-item" v-for="a in includedAlternatives" :key="a.Id" @click="showDescriptionPreview(a.Id)">
                                                        {{a.Name}}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-group">
                                            <textarea ref="descriptionTemplateTextArea" rows="5" v-model="descriptionTemplate" class="form-control"></textarea>
                                            <small class="form-text text-muted"></small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="handleConfirm" class="btn btn-primary">Save &amp; Close</button>
                        <button type="button" @click="closeDialog" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>